import * as React from 'react'
import { Camera, Loader2, Trash2 } from 'lucide-react'
import type { DocumentDto } from '@lib/dto/document.dto'
import { ALLOWED_PHOTO_CONTENT_TYPES } from '@lib/constants/documents'
import {
  useArchivePhoto,
  usePhotos,
  useUploadPhoto,
  usePhotoUrl,
  type PhotoScope,
} from '@/hooks/usePhotos'
import { errorMessage } from '@/helpers/logger'
import { uploadingLabel } from '@/helpers/uploadLabel'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Why the camera is unavailable, when it is.
 *
 * Two different reasons with two different remedies, so they get two different
 * sentences: one is "wait for signal", the other is "wait a second". Saying
 * nothing would leave a disabled button with no explanation, which reads as a
 * broken app rather than a temporary state.
 */
function renderUnavailableReason(
  isOnline: boolean,
  isPendingSync: boolean,
): React.ReactNode {
  if (!isOnline) {
    return (
      <p className="text-xs text-muted-text">
        Photos need a connection. What you record now syncs on its own, but a
        photo has to wait until you are back online.
      </p>
    )
  }
  if (isPendingSync) {
    return (
      <p className="text-xs text-muted-text">
        Saving this first — photos can be added in a moment.
      </p>
    )
  }
  return null
}

/**
 * One stored photo.
 *
 * Fetches its own view link. Storage is private — every read is a pre-signed
 * URL with a short expiry — so a thumbnail cannot point at a bucket path, and
 * the link has to be asked for per photo rather than derived.
 */
function PhotoThumb({
  photo,
  onRemove,
  isRemoving,
}: {
  photo: DocumentDto
  onRemove: () => void
  isRemoving: boolean
}) {
  const url = usePhotoUrl(photo.id)

  function renderImage(): React.ReactNode {
    // A failed link is its own state. Leaving the spinner up is how a photo
    // that is gone passes for one that is still coming down a slow connection.
    if (url.isError) {
      return (
        <div className="flex h-full items-center justify-center px-1 text-center text-[10px] leading-tight text-danger">
          Could not load
        </div>
      )
    }
    if (url.data === undefined) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    return (
      <a href={url.data} target="_blank" rel="noreferrer" className="block h-full">
        <img
          src={url.data}
          alt={photo.title ?? photo.fileName}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </a>
    )
  }

  return (
    <li className="group relative aspect-square overflow-hidden rounded-md border border-default bg-subtle">
      {renderImage()}
      <button
        type="button"
        onClick={onRemove}
        disabled={isRemoving}
        aria-label={`Remove ${photo.fileName}`}
        /*
          Always visible rather than revealed on hover. The design system's
          progressive-disclosure rule assumes a cursor; this control lives on a
          phone held in one hand in a park, where there is no hover state to
          reveal anything with.
        */
        className="absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded-md bg-map-ink/70 text-map-halo transition-opacity hover:bg-map-ink disabled:opacity-50"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  )
}

/**
 * Photographs of one survey subject: a tree, a problem on it, or an
 * irrigation record (see {@link PhotoScope}). Shared by both survey screens.
 *
 * The capture control is a plain file input with `capture`, not a custom camera
 * surface: on a phone that opens the native camera, which is faster, handles
 * orientation and HEIC, and does not need permission plumbing of its own. On a
 * laptop the same input becomes a file picker, which is the right thing there.
 *
 * Uploading is deliberately ONLINE-ONLY, unlike the rest of the survey. Records
 * are queued in an outbox and replayed, but a queue of 8 MB images is a
 * different problem — it needs a byte store, a resumable transfer, and a
 * quota conversation with the browser. Until that exists, the control says so
 * rather than silently dropping a photo somebody thought they had taken.
 */
export function SurveyPhotos({
  scope,
  isOnline,
  isPendingSync,
  controlClassName,
  label = 'Photos',
  addLabel = 'Add photo',
}: {
  /** What the photos are of — see {@link PhotoScope}. */
  scope: PhotoScope
  isOnline: boolean
  /**
   * The subject still has queued work, so the server may not know it exists.
   *
   * Survey ids are generated on the phone for offline capture, which means a
   * freshly placed tree or valve has an id that no row carries yet. Attaching
   * a photo to it would fail a foreign key — so the control waits for the
   * queue to drain, which online takes about a second.
   */
  isPendingSync: boolean
  controlClassName?: string | undefined
  /** Omitted for a subject's own photos; a problem's set says which problem. */
  label?: string | undefined
  addLabel?: string | undefined
}) {
  const canAttach = isOnline && !isPendingSync
  const photos = usePhotos(scope, { enabled: !isPendingSync })
  const [percent, setPercent] = React.useState<number | null>(null)
  const upload = useUploadPhoto(scope, setPercent)
  const archive = useArchivePhoto(scope)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const items = photos.data ?? []
  // A failed load is not an empty gallery. Add stays off until the list is
  // known, so a surveyor does not record a second copy of photos that are
  // already there, or walk away believing they are gone.

  async function handleFiles(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) return
    setError(null)
    // One at a time rather than in parallel: a survey phone is usually on a
    // rural connection, and three concurrent uploads there is slower than
    // three sequential ones as well as harder to report on.
    for (const file of Array.from(files)) {
      setPercent(null)
      try {
        await upload.mutateAsync(file)
      } catch (caught) {
        setError(errorMessage(caught, 'The photo could not be saved.'))
        break
      }
    }
    if (inputRef.current !== null) {
      // Clear it, or choosing the same file twice fires no change event.
      inputRef.current.value = ''
    }
  }

  async function handleRemove(id: string): Promise<void> {
    setError(null)
    try {
      await archive.mutateAsync(id)
    } catch (caught) {
      setError(errorMessage(caught, 'The photo could not be saved.'))
    }
  }

  function renderPhotoList(): React.ReactNode {
    if (photos.isLoading) {
      return (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (photos.isError) {
      return <p className="text-xs text-danger">{errorMessage(photos.error, 'The photo could not be saved.')}</p>
    }
    if (items.length === 0) return null
    return (
      <ul className="grid grid-cols-3 gap-1.5">
        {items.map((photo) => (
          <PhotoThumb
            key={photo.id}
            photo={photo}
            isRemoving={archive.isPending}
            onRemove={() => {
              void handleRemove(photo.id)
            }}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>

      {renderPhotoList()}

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_PHOTO_CONTENT_TYPES.join(',')}
        // `environment` asks for the rear camera, which is the one pointed at
        // a tree. Ignored on a desktop, where this is just a file picker.
        capture="environment"
        multiple
        className="hidden"
        onChange={(event) => {
          void handleFiles(event.target.files)
        }}
      />

      <Button
        variant="outline"
        className={cn('w-full', controlClassName)}
        disabled={!canAttach || upload.isPending || !photos.isSuccess}
        onClick={() => {
          inputRef.current?.click()
        }}
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Camera className="h-4 w-4" />
        )}
        {upload.isPending ? uploadingLabel(percent) : addLabel}
      </Button>

      {renderUnavailableReason(isOnline, isPendingSync)}
      {error === null ? null : <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}
